import {createAuth0Client} from '@auth0/auth0-spa-js'
import {environments} from '@/constants/app.js'
import {useEnv} from '@/composables/use-env.js'
import {shallowRef, computed} from 'vue'

//Auth0 configs per environment
const auth0Configs = {
  [environments.LOCAL]: {
    domain: 'arist-development.us.auth0.com',
    clientId: 'dWC4O7x2c0eMmgQPi202sGUdZvGj80jA',
  },
  [environments.DEV]: {
    domain: 'arist-development.us.auth0.com',
    clientId: 'dWC4O7x2c0eMmgQPi202sGUdZvGj80jA',
  },
  [environments.QA]: {
    domain: 'arist-qa.us.auth0.com',
    clientId: 'KudnydzlgtSPluJKXHHRIYwBuLrxks2n',
  },
  [environments.STAGING]: {
    domain: 'arist-staging.us.auth0.com',
    clientId: 'iySuEhWsZLn1ZV2J8fEEYgeQBcOqr2zw',
  },
  [environments.PRODUCTION]: {
    domain: 'arist.us.auth0.com',
    clientId: 'qWMM2TKQglytTatD953ZGRlwad3f7tmA',
  },
}

//Global ref so the client is shared across composable calls
const auth0Client = shallowRef(null)

/**
 * Auth0 composable
 */
export function useAuth0() {

  //Initialize Auth0 client
  const initializeAuth0 = async () => {
    const {env} = useEnv()
    const {domain, clientId} = auth0Configs[env.value]
    auth0Client.value = await createAuth0Client({
      domain,
      clientId,
      authorizationParams: {
        redirect_uri: window.location.origin,
      },
    })
  }

  //Check if the URL looks like an Auth0 redirect callback (OAuth code + state)
  const hasAuth0RedirectCallbackParams = computed(() => {
    return (
      window?.location?.search.includes('code=') &&
      window?.location?.search.includes('state=')
    )
  })

  //Handle redirect callback after Auth0 login (cleans code/state from URL)
  const handleAuth0Callback = async () => {
    if (hasAuth0RedirectCallbackParams.value) {
      await auth0Client.value.handleRedirectCallback()
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }

  //Get ID token — used as bearer for Arist API (validated by OmniAuth::Auth0::JWTValidator)
  //Triggers a silent refresh if the token is expired but the Auth0 session is still valid.
  //Throws login_required if the session has expired (caller should redirect to login)
  const getAuth0Token = async () => {
    await auth0Client.value.getTokenSilently()
    const claims = await auth0Client.value.getIdTokenClaims()
    return claims?.__raw ?? null
  }

  //Redirect to Auth0 login page (lazily initialize the client if it was never set
  //up, e.g. when re-authenticating after a cookie-authenticated session expires)
  const loginWithAuth0 = async () => {
    if (!auth0Client.value) {
      await initializeAuth0()
    }
    return auth0Client.value.loginWithRedirect({
      appState: {returnTo: window.location.pathname},
    })
  }

  //Log out via Auth0 (clears Auth0 session and redirects)
  const logoutWithAuth0 = async () => {
    if (!auth0Client.value) {
      await initializeAuth0()
    }
    return auth0Client.value.logout({
      logoutParams: {returnTo: window.location.origin},
    })
  }

  //Expose interface
  return {
    initializeAuth0,
    handleAuth0Callback,
    hasAuth0RedirectCallbackParams,
    getAuth0Token,
    loginWithAuth0,
    logoutWithAuth0,
  }
}
